import { supabase } from './supabase';

/**
 * Create a geometry point using the helper function
 * @param {number} lat - Latitude
 * @param {number} lon - Longitude
 * @returns {Promise<Object>} Geometry point object
 */
export const createGeometryPoint = async (lat, lon) => {
  try {
    const { data, error } = await supabase
      .rpc('make_geometry_point', { lat, lon });

    if (error) {
      setTimeout(() => {
        throw error;
      }, 0);
      throw error;
    }
    return data;
  } catch (error) {
    setTimeout(() => {
      throw error;
    }, 0);
    throw error;
  }
};


/**
 * Find nearby toilets using helper functions
 * @param {number} userLat - User latitude
 * @param {number} userLon - User longitude
 * @param {number} maxDistance - Maximum distance in kilometers
 * @param {number} limit - Maximum number of results
 * @returns {Promise<Array>} Array of nearby toilets
 */
export const findNearbyToilets = async (userLat, userLon, maxDistance = 2.5, limit = 20) => {
  try {
    // Use the new coordinates-based function
    const { data, error } = await supabase
      .rpc('find_nearby_toilets_coords', {
        user_lat: parseFloat(userLat),
        user_lon: parseFloat(userLon),
        max_distance: parseFloat(maxDistance),
        limit_count: parseInt(limit)
      });

    if (error) {
      setTimeout(() => {
        throw new Error(`Database search error: ${error.message || error}`);
      }, 0);
      throw new Error(`Database search error: ${error.message || error}`);
    }

    return data || [];
  } catch (error) {
    setTimeout(() => {
      throw new Error(`Database search exception: ${error.message || error}`);
    }, 0);
    throw new Error(`Database search exception: ${error.message || error}`);
  }
};

/**
 * Get opening hours for multiple toilets
 * @param {Array} toiletIds - Array of toilet IDs
 * @returns {Promise<Object>} Object with toilet IDs as keys and opening hours as values
 */
export const getMultipleToiletOpeningHours = async (toiletIds) => {
  try {
    if (!toiletIds || toiletIds.length === 0) {
      return {};
    }

    const { data, error } = await supabase
      .from('opening_hours')
      .select('id, dow, open_time, close_time')
      .in('id', toiletIds)
      .order('id, dow');

    if (error) {
      setTimeout(() => {
        throw error;
      }, 0);
      throw error;
    }

    // Group by toilet ID - each toilet can have multiple entries (one per day)
    const openingHoursByToilet = {};
    data.forEach(hour => {
      if (!openingHoursByToilet[hour.id]) {
        openingHoursByToilet[hour.id] = [];
      }
      openingHoursByToilet[hour.id].push(hour);
    });


    return openingHoursByToilet;
  } catch (error) {
    setTimeout(() => {
      throw error;
    }, 0);
    throw error;
  }
};


// Function to generate a unique ID in the format: 82e52e86fbd2927389f9a2e3
const generateToiletId = () => {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};

// Function to add a new toilet to the database
export const addToilet = async (toiletData, openingHoursData) => {
  try {
    // Generate a unique ID for the toilet
    const toiletId = generateToiletId();

    // Add the ID to the toilet data
    const toiletDataWithId = {
      ...toiletData,
      id: toiletId
    };

    // First, insert the toilet with the generated ID
    const { data: toiletResult, error: toiletError } = await supabase
      .from('toilets')
      .insert([toiletDataWithId])
      .select();

    if (toiletError) {
      setTimeout(() => {
        throw toiletError;
      }, 0);
      throw new Error(`Failed to insert toilet: ${toiletError.message}`);
    }

    // If we have opening hours data and the toilet was inserted successfully
    if (openingHoursData && openingHoursData.length > 0) {
      // Add the toilet id to each opening hours record
      const openingHoursWithId = openingHoursData.map(hour => ({
        ...hour,
        id: toiletId
      }));

      const { error: hoursError } = await supabase
        .from('opening_hours')
        .insert(openingHoursWithId)
        .select();

      if (hoursError) {
        setTimeout(() => {
          throw hoursError;
        }, 0);
      } else {
        // no-op
      }
    }

    return {
      success: true,
      toilet: toiletResult[0],
      message: 'Toilet added successfully'
    };

  } catch (error) {
    setTimeout(() => {
      throw error;
    }, 0);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Update toilet address in the database
 * @param {number} toiletId - Toilet ID
 * @param {string} address - Resolved address
 * @returns {Promise<Object>} Update result
 */
export const updateToiletAddress = async (toiletId, address) => {
  try {
    const { data, error } = await supabase
      .from('toilets')
      .update({ address: address })
      .eq('id', toiletId)
      .select();

    if (error) {
      setTimeout(() => {
        throw error;
      }, 0);
      throw error;
    }
    return data;
  } catch (error) {
    setTimeout(() => {
      throw error;
    }, 0);
    throw error;
  }
};

const toiletService = {
  createGeometryPoint,
  findNearbyToilets,
  addToilet,
  getMultipleToiletOpeningHours,
  updateToiletAddress
};
export default toiletService;
